import React, { useState, useEffect } from 'react';
import { InputAdornment, IconButton } from '@mui/material';
import { Search as SearchIcon, Clear as ClearIcon } from '@mui/icons-material';
import TextField from './TextField';
import useDebounce from '../../utils/hooks/useDebounce';

/**
 * SearchInput — universal search field with debounce and clear button.
 *
 * This component is a drop-in replacement for MUI TextField for search use cases.
 * All MUI TextField props are supported.
 *
 * @component
 * @example
 * // Basic usage
 * <SearchInput
 *   value={search}
 *   onChange={setSearch}
 *   placeholder="Search..."
 *   debounceTime={300}
 *   size="small"
 *   sx={{ mb: 2 }}
 * />
 *
 * @param {object} props - All MUI TextField props are supported, plus:
 * @param {string} props.value - Current value of the input
 * @param {function} props.onChange - Change handler (called with value)
 * @param {string} [props.placeholder] - Placeholder text
 * @param {number} [props.debounceTime=300] - Debounce delay in ms
 */

const SearchInput = ({
  value,
  onChange,
  placeholder,
  debounceTime = 300,
  ...props
}) => {
  const [localValue, setLocalValue] = useState(value);
  const debouncedOnChange = useDebounce((value) => {
    onChange(value);
  }, debounceTime);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const handleChange = (e) => {
    const newValue = e.target.value;
    setLocalValue(newValue);
    debouncedOnChange(newValue);
  };

  const handleClear = () => {
    setLocalValue('');
    onChange('');
  };

  return (
    <TextField
      fullWidth
      value={localValue}
      onChange={handleChange}
      placeholder={placeholder}
      {...props}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon sx={{ color: 'text.secondary' }} />
          </InputAdornment>
        ),
        endAdornment: localValue ? (
          <InputAdornment position="end">
            <IconButton
              aria-label="clear search"
              onClick={handleClear}
              edge="end"
              size="small"
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          </InputAdornment>
        ) : null,
        ...(props.InputProps || {}),
      }}
    />
  );
};

export default SearchInput;
